import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';

export interface TourSession {
  id: string;
  name: string;
  email: string;
  phone: string;
  building?: string;
  buildingName?: string;
  buildingAddress?: string;
  apartmentNumber?: string;
  authCode?: string;
  authCodeExpiry?: number;
  authCodeAttempts?: number;
  authCodeBlockedUntil?: number;
  verifyingAgentId?: string;
  startTime?: number;
  endTime?: number;
  status: 'check-in' | 'building-selection' | 'apartment-entry' | 'active' | 'completed';
  deviceAccess?: string[];
  createdAt: number;
  updatedAt: number;
}

export interface Building {
  id: string;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  units?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ToursService {
  private currentTourSession = new BehaviorSubject<TourSession | null>(null);
  public tourSession$ = this.currentTourSession.asObservable();

  // Test mode flag - set to true to bypass authorization
  private testModeEnabled = false;

  constructor(
    private http: HttpClient
  ) {
    this.loadStoredSession();
    // Check for test mode in session storage
    const testMode = sessionStorage.getItem('tourTestMode');
    this.testModeEnabled = testMode === 'true';
  }

  private loadStoredSession() {
    const storedSession = localStorage.getItem('tourSession');
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession) as TourSession;
        // Check if session is still valid (within 24 hours)
        const dayInMs = 24 * 60 * 60 * 1000;
        if (Date.now() - session.createdAt < dayInMs) {
          this.currentTourSession.next(session);
        } else {
          localStorage.removeItem('tourSession');
          this.currentTourSession.next(null);
        }
      } catch (error) {
        console.error('Error loading stored tour session:', error);
        localStorage.removeItem('tourSession');
        this.currentTourSession.next(null);
      }
    } else {
      // Ensure BehaviorSubject is cleared if no session
      this.currentTourSession.next(null);
    }
  }

  // Method to clear session (useful for testing)
  clearSession(): void {
    localStorage.removeItem('tourSession');
    sessionStorage.removeItem('tourAgentCount');
    this.currentTourSession.next(null);
    console.log('[Tours] Session cleared');
  }

  private saveSession(session: TourSession) {
    this.currentTourSession.next(session);
    localStorage.setItem('tourSession', JSON.stringify(session));
  }

  async createTourSession(name: string, email: string, phone: string): Promise<TourSession> {
    const session: TourSession = {
      id: uuidv4(),
      name,
      email,
      phone,
      status: 'check-in',
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    // Note: Tour sessions are stored locally only.
    // DynamoDB operations are handled via Lambda functions when auth codes are requested/verified.

    this.saveSession(session);
    return session;
  }

  async updateTourSession(updates: Partial<TourSession>): Promise<TourSession | null> {
    const currentSession = this.currentTourSession.value;
    if (!currentSession) {
      return null;
    }

    const updatedSession: TourSession = {
      ...currentSession,
      ...updates,
      updatedAt: Date.now()
    };

    // Note: Tour session updates are stored locally only.
    // DynamoDB operations are handled via Lambda functions when needed.

    this.saveSession(updatedSession);
    return updatedSession;
  }

  async selectBuilding(buildingId: string, buildingName: string, buildingAddress: string): Promise<TourSession | null> {
    return this.updateTourSession({
      building: buildingId,
      buildingName,
      buildingAddress,
      status: 'building-selection'
    });
  }

  async setApartmentNumber(apartmentNumber: string): Promise<TourSession | null> {
    return this.updateTourSession({
      apartmentNumber,
      status: 'apartment-entry'
    });
  }

  async verifyAuthorizationCode(code: string): Promise<{ success: boolean; message: string; agent?: any }> {
    const session = this.currentTourSession.value;
    if (!session) {
      return { success: false, message: 'No active tour session' };
    }

    try {
      // Call Lambda function to verify code
      const lambdaUrl = (environment as any).tourVerifyCodeLambdaUrl;
      if (!lambdaUrl) {
        console.error('Tour verify code Lambda URL not configured');
        return { success: false, message: 'Verification service not configured' };
      }

      const response = await firstValueFrom(
        this.http.post<any>(lambdaUrl, {
          tourSessionId: session.id,
          code
        }, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json'
          })
        })
      );

      if (response.success) {
        // Update local session with verification details
        await this.updateTourSession({
          status: 'active',
          startTime: Date.now(),
          verifyingAgentId: response.agent?.id,
          authCodeAttempts: 0
        });

        return {
          success: true,
          message: 'Tour authorized successfully',
          agent: response.agent
        };
      } else {
        return {
          success: false,
          message: response.error || 'Invalid authorization code'
        };
      }
    } catch (error: any) {
      console.error('Error verifying auth code:', error);

      // Handle specific error responses
      if (error.status === 429) {
        return { success: false, message: error.error?.error || 'Too many attempts. Please try again later.' };
      } else if (error.status === 401) {
        return { success: false, message: error.error?.error || 'Invalid or expired authorization code' };
      } else if (error.status === 404) {
        return { success: false, message: 'No authorization codes found for this session' };
      }

      return { success: false, message: 'Failed to verify authorization code' };
    }
  }

  async requestAuthorizationCode(): Promise<{ success: boolean; message: string; agentCount?: number }> {
    const session = this.currentTourSession.value;
    if (!session || !session.apartmentNumber || !session.building) {
      return { success: false, message: 'Please complete building and apartment selection first' };
    }

    try {
      // Call Lambda function to generate and send codes
      const lambdaUrl = (environment as any).tourAuthCodeLambdaUrl;
      if (!lambdaUrl) {
        console.error('Tour auth code Lambda URL not configured');
        return { success: false, message: 'Authorization service not configured' };
      }

      const response = await firstValueFrom(
        this.http.post<any>(lambdaUrl, {
          tourSessionId: session.id,
          building: session.building,
          buildingName: session.buildingName,
          apartmentNumber: session.apartmentNumber,
          residentName: session.name,
          residentEmail: session.email,
          residentPhone: session.phone
        }, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json'
          })
        })
      );

      if (response.success) {
        // Update local session with auth code request details
        await this.updateTourSession({
          authCodeExpiry: response.expiresAt,
          authCodeAttempts: 0,
          authCodeBlockedUntil: undefined
        });

        return {
          success: true,
          message: response.message || `Authorization codes sent to ${response.agentCount} agent(s)`,
          agentCount: response.agentCount
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to send authorization codes'
        };
      }
    } catch (error: any) {
      console.error('Error requesting auth code:', error);

      // Handle specific error responses
      if (error.status === 404) {
        return { success: false, message: 'No tour agents available for this building' };
      } else if (error.status === 429) {
        return { success: false, message: 'Too many requests. Please try again later.' };
      }

      return { success: false, message: 'Failed to send authorization code' };
    }
  }

  async getBuildings(): Promise<Building[]> {
    const lambdaUrl = (environment as any).tourGetBuildingsLambdaUrl;

    if (!lambdaUrl) {
      console.error('Tour get buildings Lambda URL not configured');
      return [];
    }

    console.log('Fetching buildings from Lambda:', lambdaUrl);

    try {
      // Get user's location - REQUIRED for Tours
      let userLocation: { latitude?: number; longitude?: number } = {};

      if (!('geolocation' in navigator)) {
        throw new Error('Geolocation is not supported by your browser');
      }

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 10000,
            enableHighAccuracy: true
          });
        });
        userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        console.log('Location obtained:', userLocation);
      } catch (locationError) {
        // Location is REQUIRED for tours - throw error
        console.error('Location access denied or unavailable:', locationError);
        throw new Error('Location permission is required to continue with the tour. Please enable location services and try again.');
      }

      // Call Lambda to get buildings
      const response = await firstValueFrom(
        this.http.post<any>(lambdaUrl, userLocation, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json'
          })
        })
      );

      if (response.success && response.buildings && response.buildings.length > 0) {
        console.log('Buildings from Lambda:', response.buildings);
        if (!response.locationProvided) {
          console.log('Location not available - showing all buildings with tour agents');
        }
        return response.buildings;
      }

      // If no buildings found, return empty array
      console.warn('No buildings found from Lambda:', response.message || 'No buildings available');
      return [];
    } catch (error) {
      console.error('Error fetching buildings from Lambda:', error);
      // Return empty array instead of test data
      return [];
    }
  }


  async endTour(): Promise<void> {
    const session = this.currentTourSession.value;
    if (session) {
      await this.updateTourSession({
        status: 'completed',
        endTime: Date.now()
      });
    }
    this.clearSession();
  }


  getCurrentSession(): TourSession | null {
    return this.currentTourSession.value;
  }

  isInTourMode(): boolean {
    const session = this.currentTourSession.value;
    return session !== null && session.status !== 'completed';
  }

  // Test mode methods
  enableTestMode(): void {
    this.testModeEnabled = true;
    sessionStorage.setItem('tourTestMode', 'true');
    console.log('Tour test mode ENABLED - authorization bypass active');
  }

  disableTestMode(): void {
    this.testModeEnabled = false;
    sessionStorage.removeItem('tourTestMode');
    console.log('Tour test mode DISABLED - normal authorization required');
  }

  isTestModeEnabled(): boolean {
    return this.testModeEnabled;
  }

  // Create a test session for quick access to tour walkthrough
  createTestSession(): void {
    if (!this.testModeEnabled) {
      console.warn('Test mode is not enabled. Call enableTestMode() first.');
      return;
    }

    const testSession: TourSession = {
      id: 'test-' + uuidv4(),
      name: 'Test User',
      email: 'test@vessel.com',
      phone: '(555) 123-4567',
      building: 'test-building',
      buildingName: 'Test Building',
      buildingAddress: '123 Test St, Test City',
      apartmentNumber: 'TEST-101',
      authCode: '000000',
      status: 'active',
      startTime: Date.now(),
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    this.currentTourSession.next(testSession);
    localStorage.setItem('tourSession', JSON.stringify(testSession));
    console.log('Test tour session created - skipping to active tour');
  }
}
